import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase";
import type { UserProfile } from "../types";

interface AuthContextValue {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** True inside an installed PWA or the Android TWA wrapper. */
function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

// The *currently shipped* Android wrapper (TripTrackerWearOS `main`, `phone`
// module) does native Google Sign-In, exchanges the ID token at
// /api/auth/custom-token, and launches the TWA at
// `${TWA_URL}?firebaseToken=…`. The old app consumed that param; without this
// the TWA lands signed out on the new app and falls back to signInWithPopup —
// exactly the Custom Tab flow the native handoff exists to avoid.
//
// `staging/overhaul` drops the param (the phone lets the web app own auth), so
// this becomes a no-op once that ships. Keep it until installs have rolled
// over: the domain cutover reaches every existing install immediately, an app
// update does not.
//
// Read at module load, synchronously, so the provider knows a sign-in is
// inbound before React can render the signed-out Login page.
const handoffToken: string | null = (() => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const token = params.get("firebaseToken");
  if (!token) return null;
  // Strip the credential from the URL immediately so it never lands in history,
  // a referrer header, or an error report.
  params.delete("firebaseToken");
  const query = params.toString();
  window.history.replaceState(
    {},
    "",
    window.location.pathname + (query ? `?${query}` : "") + window.location.hash
  );
  return token;
})();

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  // Held true until the native handoff resolves, so the app doesn't flash the
  // Login page between onAuthStateChanged(null) and the custom-token sign-in.
  const [handoffPending, setHandoffPending] = useState(handoffToken !== null);
  const handoffStarted = useRef(false);

  useEffect(() => {
    if (!handoffToken || handoffStarted.current) return;
    handoffStarted.current = true;
    signInWithCustomToken(auth, handoffToken)
      .catch((error) => {
        // Falling through leaves the user on the Login page rather than a
        // blank screen; the token is short-lived and may simply have expired.
        console.error("Native sign-in handoff failed:", error);
      })
      .finally(() => setHandoffPending(false));
  }, []);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      if (!firebaseUser) setUserProfile(null);
      // Resolve loading as soon as the auth state is known — don't block the
      // whole app on the profile document loading (which, on a cold/flaky
      // start, may lag behind). The profile fills in async via the listener
      // below; components read it defensively (userProfile?.…).
      setLoading(false);
    });

    return unsubAuth;
  }, []);

  // Watch the profile document. The `beforesignin` blocking function owns it
  // — it creates the doc on first sign-in and refreshes displayName, avatarUrl
  // and lastActive on every later one, all before sign-in completes. So by the
  // time this listener attaches the document is already there and current, and
  // the client has no reason to write it. That matters beyond tidiness: while
  // the client did the writing, `enabled` and `roles` were being set by the
  // account they govern.
  useEffect(() => {
    if (!user) return;

    const userRef = doc(db, "users", user.uid);

    const unsubProfile = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          setUserProfile(snapshot.data() as UserProfile);
        }
      },
      () => {
        // Error reading profile — non-fatal; the app already rendered.
      }
    );

    return unsubProfile;
  }, [user]);

  const signInWithGoogle = async () => {
    await signInWithPopup(auth, googleProvider);
  };

  const logout = async () => {
    // Hand back to the wrapper *before* awaiting signOut. signOut flips
    // onAuthStateChanged, which re-renders Layout into the web Login screen --
    // so doing this afterwards paints that screen for a frame before the native
    // activity comes forward. Navigating to a custom scheme hands the intent to
    // the OS without unloading the page, so the sign-out below still runs.
    //
    // In the TWA the native wrapper holds its own Firebase/Google session,
    // independent of this one. Without the hand-back only the web side clears:
    // the native uid persists, so location uploads and the watch's custom token
    // (minted from that session) stay on the previous account while the UI
    // shows the new one. Both wrapper branches register the scheme and route it
    // to LoginActivity's sign-out path.
    //
    // This also fires for an installed desktop/mobile PWA, where the scheme
    // won't resolve and the navigation no-ops. That matches the old app's
    // Drawer.jsx; the user is signed out either way, so the only cost is not
    // being bounced to /login.
    if (isStandalone()) {
      window.location.href = "triptracker://logout";
    }
    await signOut(auth);
    setUserProfile(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, userProfile, loading: loading || handoffPending, signInWithGoogle, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
